import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { PageLayout } from '@/components/shared/PageLayout';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { SimpleSelect } from '@/components/form/inputs/SimpleSelect';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card/Card';
import { IconButton } from '@/components/ui/icon-button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { useReferenceAlias } from '@/hooks/useReferenceAlias';
import { useApiClient } from '@/hooks/useApiClient';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import { errorResult } from '@/lib/http/runJob';
import { JobRequestHandler } from '@/lib/http/JobRequestHandler';
import type { Result } from '@/lib/http/HttpClient';
import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import { type Tool } from '@/features/traceability/type/Tool';
import { useTranslation } from '@/hooks/useTranslation';
import { useApiCall } from '@/hooks/useApiCall';
import { buildLeaderboardFromTools } from '../utils/tool';
import { sanitizeReference } from '../utils/reference';
import { computeToolDashboardEntries, computeReferenceStats } from '../utils/dashboard';
import { usePanelState } from '../hooks/usePanelState';
import { PANEL_REGISTRY } from '../components/dashboard/panelRegistry';
import { PanelDrawer } from '../components/dashboard/PanelDrawer';
import { ToolLivesPanel } from '../components/dashboard/ToolLivesPanel';
import { GroupedBarsPanel } from '../components/dashboard/GroupedBarsPanel';
import { ScatterPanel } from '../components/dashboard/ScatterPanel';
import { AnomaliesPanel } from '../components/dashboard/AnomaliesPanel';
import { KpiStrip } from '../components/dashboard/KpiStrip';
import { ComplexCharts } from '../components/shared/ComplexCharts';
import type { AnalyticsErrorDetails } from '../types';
import { finalStateTone } from '../utils/stateTone';
import { cn } from '@/lib/utils';
import { StorageService } from '@/lib/StorageService';

const LAST_REF_KEY = 'analytics_dashboard_last_ref';
const storage = new StorageService();

function PanelCard({
  title,
  full,
  onClose,
  children,
}: {
  title: string;
  full: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Card padding="sm" className={cn('flex flex-col gap-3 group', full && 'col-span-2')}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-foreground">{title}</span>
        <IconButton
          icon={X}
          label={t('common.removeItem', { item: title })}
          onClick={onClose}
          className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        />
      </div>
      {children}
    </Card>
  );
}

export function AnalyticsDashboard() {
  const http = useApiClient();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resolve } = useReferenceAlias();

  const breadcrumbItems = [
    { label: t('common.brand'), onClick: () => navigate('/') },
    { label: t('analytics.dashboard.breadcrumb.analytics') },
  ];

  const [tools, setTools] = useState<Tool[]>([]);
  const [references, setReferences] = useState<string[]>([]);
  const [reference, setReference] = useState<string>('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const preservedReferenceRef = useRef<string | null>(null);

  const { panelState, toggle, set } = usePanelState();

  const traceabilityApiClient = useMemo(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  const fetchTools = useCallback(
    async (signal: AbortSignal) => {
      if (!traceabilityApiClient) {
        return errorResult('TraceabilityApiClient not initialized', 'InitializationError');
      }
      return new Promise<Result<AllToolsResponse>>((resolve) => {
        let settled = false;
        const handler = new JobRequestHandler<AllToolsResponse>({
          initFn: () => traceabilityApiClient.toolReadAll(undefined, { signal }),
          statusFn: (jobId) => traceabilityApiClient.toolsStatus({ jobId }, { signal }),
          signal,
          setResult: (result) => {
            if (settled || !result) return;
            settled = true;
            if (!result.ok) {
              const errorInfo: AnalyticsErrorDetails = {
                name: result.name ?? null,
                code: result.code ? String(result.code) : null,
                message: result.message ?? '',
                timestamp: result.timestamp ?? new Date().toISOString(),
              };
              console.error('Failed to load tools', errorInfo);
              resolve({ ok: false, code: result.code ?? 500, name: result.name ?? 'UnknownError', message: result.message ?? '', timestamp: result.timestamp ?? new Date().toISOString() });
              return;
            }
            resolve(result);
          },
        });
        handler.start().catch((err) => {
          if (settled) return;
          settled = true;
          resolve({ ok: false, code: 500, name: err instanceof Error ? err.name : 'UnknownError', message: err instanceof Error ? err.message : String(err), timestamp: new Date().toISOString() });
        });
      });
    },
    [traceabilityApiClient]
  );

  const { isLoading } = useApiCall<AllToolsResponse>({
    apiFn: fetchTools,
    deps: [refreshTick],
    immediate: !!traceabilityApiClient,
    onSuccess: (data) => {
      const fetchedTools = data.tools.filter((tool): tool is Tool => !!tool);
      setTools(fetchedTools.map((tool) => ({ ...tool, traces: Array.isArray(tool.traces) ? tool.traces : [] })));
    },
    onError: () => {
      setTools([]);
      setReferences([]);
      setReference('');
      preservedReferenceRef.current = null;
    },
  });

  useEffect(() => {
    if (!tools.length) {
      setReferences([]);
      setReference('');
      preservedReferenceRef.current = null;
      return;
    }
    const dataset = buildLeaderboardFromTools(tools);
    setReferences(dataset.labels);
    setReference((prev) => {
      const preservedNorm = preservedReferenceRef.current ? sanitizeReference(preservedReferenceRef.current) : null;
      if (preservedNorm && dataset.labels.includes(preservedNorm)) { preservedReferenceRef.current = preservedNorm; return preservedNorm; }
      const prevNorm = sanitizeReference(prev);
      if (prevNorm && dataset.labels.includes(prevNorm)) { preservedReferenceRef.current = prevNorm; return prevNorm; }
      const stored = sanitizeReference(storage.getPreference(LAST_REF_KEY));
      if (stored && dataset.labels.includes(stored)) { preservedReferenceRef.current = stored; return stored; }
      const fallback = dataset.labels[0] ?? '';
      preservedReferenceRef.current = fallback || null;
      return fallback;
    });
  }, [tools]);

  const entries = useMemo(() => computeToolDashboardEntries(tools, reference), [tools, reference]);
  const refStats = useMemo(() => computeReferenceStats(entries), [entries]);

  const handleRefresh = useCallback(() => {
    preservedReferenceRef.current = sanitizeReference(reference);
    setRefreshTick((v) => v + 1);
  }, [reference]);

  const handleReferenceChange = useCallback((value: string) => {
    const sanitized = sanitizeReference(value) ?? '';
    preservedReferenceRef.current = sanitized;
    setReference(sanitized);
    if (sanitized) storage.setPreference(LAST_REF_KEY, sanitized);
  }, []);

  const activePanels = PANEL_REGISTRY.filter((p) => panelState[p.id] ?? p.defaultOn);

  return (
    <PageLayout contentClassName="max-w-7xl">
      <div className="flex flex-col gap-6">
        <PageHeader
          breadcrumb={breadcrumbItems}
          title={t('analytics.dashboard.title')}
          description={t('analytics.dashboard.subtitle')}
          actions={
            <Button variant="outline" size="sm" onClick={() => setDrawerOpen(true)}>
              {t('analytics.dashboard.managepanels')}
            </Button>
          }
        />

      <Card padding="none" className="flex flex-wrap items-center gap-3 px-4 py-3">
        <Label htmlFor="analytics-dashboard-reference" className="shrink-0 text-sm font-medium">
          {t('analytics.dashboard.ref.label')}
        </Label>
        <SimpleSelect
          id="analytics-dashboard-reference"
          value={reference}
          onChange={(value) => handleReferenceChange(value ?? '')}
          options={references.map((ref) => ({ value: ref, label: resolve(ref).label }))}
          placeholder={t('form.select.options')}
          className="w-auto min-w-48 max-w-md"
        />
        <div className="flex flex-wrap gap-2 ml-2">
          <Badge>
            {refStats.totalTools} {t('analytics.dashboard.ref.tools')}
          </Badge>
          <Badge>
            {Math.round(refStats.avgPieces)} {t('analytics.dashboard.ref.avgPieces')}
          </Badge>
          {Object.entries(refStats.stateCounts).map(([state, count]) => (
            <Badge key={state} tone={finalStateTone(state)}>
              {count} {t(`analytics.dashboard.ref.states.${state}`, { defaultValue: state })}
            </Badge>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Button variant="outline" size="sm" onClick={handleRefresh} loading={isLoading}>
            {t('analytics.dashboard.refresh')}
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to="/analytics/classic">{t('analytics.dashboard.classicView')}</Link>
          </Button>
        </div>
      </Card>

      {refStats.totalTools > 0 && <KpiStrip stats={refStats} />}

      {isLoading && (
        <div role="status" className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
          <Spinner />
          {t('analytics.dashboard.loading')}
        </div>
      )}

      {!isLoading && !reference && <EmptyState title={t('analytics.dashboard.error.noRef')} />}

      {!isLoading && reference && activePanels.length === 0 && (
        <EmptyState
          title={t('analytics.dashboard.panel.noselection.title')}
          description={t('analytics.dashboard.panel.noselection.desc')}
        />
      )}

      {!isLoading && reference && activePanels.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {activePanels.map((panel) => (
            <PanelCard
              key={panel.id}
              title={t(panel.nameKey)}
              full={panel.full}
              onClose={() => set(panel.id, false)}
            >
              {panel.id === 'lives' && <ToolLivesPanel entries={entries} />}
              {panel.id === 'anomalies' && <AnomaliesPanel entries={entries} avgPieces={refStats.avgPieces} />}
              {panel.id === 'machine' && <GroupedBarsPanel entries={entries} groupBy="machine" noteKey="analytics.dashboard.grouped.note.machine" />}
              {panel.id === 'operator' && <GroupedBarsPanel entries={entries} groupBy="operator" noteKey="analytics.dashboard.grouped.note.operator" />}
              {panel.id === 'material' && <GroupedBarsPanel entries={entries} groupBy="material" noteKey="analytics.dashboard.grouped.note.material" />}
              {panel.id === 'vc' && <ScatterPanel entries={entries} axis="vc" noteKey="analytics.dashboard.scatter.note.vc" />}
              {panel.id === 'fz' && <ScatterPanel entries={entries} axis="fz" noteKey="analytics.dashboard.scatter.note.fz" />}
              {panel.id === 'ap' && <ScatterPanel entries={entries} axis="ap" noteKey="analytics.dashboard.scatter.note.ap" />}
            </PanelCard>
          ))}
        </div>
      )}

      <div className="border-t border-border" />
      <ComplexCharts tools={tools} isLoading={isLoading} mode="analytics" />

      <PanelDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        panelState={panelState}
        onToggle={toggle}
      />
    </div>
  </PageLayout>
  );
}
